import { useState, useEffect, useCallback } from "react";
import api from "../../../../conf/api";
import { templateChoices, templateSlots } from "./templateSlots";

export default function useBrandTemplates(brandName) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandName) return;
    setLoading(true);
    setError(null);
    api
      .get(`/mrss/brand-templates/?brand=${encodeURIComponent(brandName)}`)
      .then((res) => setTemplates(res.data?.results ?? res.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [brandName]);

  // The default row: the only row, unless YouTube has a second template and
  // that one was made the default (hooks/templateSlots.js).
  const getTemplate = useCallback(
    (platform, orientation = "vertical") => templateSlots(templates, platform, orientation).default,
    [templates]
  );
  /** YouTube's main/second rows when it has a second template, else null. */
  const getTemplateChoices = useCallback((platform) => templateChoices(templates, platform), [templates]);

  return { templates, loading, error, getTemplate, getTemplateChoices };
}

/**
 * The same assigned templates for several brands at once (the batch editor).
 * `getTemplate(brand, platform, orientation)`; `loading` until every brand has
 * answered, so a tab never opens the gallery just because its row is late.
 */
export function useBrandsTemplates(brandNames, enabled = true) {
  const [byBrand, setByBrand] = useState({});
  const [pending, setPending] = useState(0);
  const namesKey = brandNames.join("\u0000");

  useEffect(() => {
    if (!enabled) return undefined;
    const names = namesKey ? namesKey.split("\u0000") : [];
    let live = true;
    setPending(names.length);
    names.forEach((name) => {
      api
        .get(`/mrss/brand-templates/?brand=${encodeURIComponent(name)}`)
        .then((res) => live && setByBrand((prev) => ({ ...prev, [name]: res.data?.results ?? res.data ?? [] })))
        // A brand whose templates cannot load behaves as one with none assigned.
        .catch(() => live && setByBrand((prev) => ({ ...prev, [name]: [] })))
        .finally(() => live && setPending((n) => n - 1));
    });
    return () => {
      live = false;
    };
  }, [namesKey, enabled]);

  const getTemplate = useCallback(
    (brandName, platform, orientation = "vertical") => templateSlots(byBrand[brandName], platform, orientation).default,
    [byBrand]
  );
  const getTemplateChoices = useCallback((brandName, platform) => templateChoices(byBrand[brandName], platform), [byBrand]);

  return { loading: pending > 0, getTemplate, getTemplateChoices };
}
